import { useEffect } from "react"
import { useQueryClient } from "@tanstack/react-query"
import { versionedImageUrl } from "@/lib/image-version"
import { Link, useNavigate } from "@tanstack/react-router"
import type { ToolUIPart } from "ai"
import { ArrowRight, Compass } from "lucide-react"

import { Button } from "@/components/ui/button"
import { useEssenceEmpfehlung } from "@/lib/essence-empfehlung"
import { learningEssenceControllerGetQueryKey } from "@/client/@tanstack/react-query.gen"
import { useCoach } from "@/lib/use-coach"
import { GESPRAECHSTRAINING_LABEL } from "@nl/shared"

/** Mirror of the tool result in `apps/api/src/ai/onboarding-tools.ts`. */
type Abschluss = { abgeschlossen: boolean; grund?: string }

/**
 * The granted onboarding completion as something to click, under the coach's
 * closing message: straight into the recommended training, another training,
 * or just look around first.
 *
 * Only the GRANTED completion renders — a refusal is conversational material
 * for the coach, not a card. The choice stays with the advisor: nothing starts
 * that nobody clicked.
 */
export function OnboardingCompleteCard({ part }: { part: ToolUIPart }) {
  const coach = useCoach()
  const navigate = useNavigate()
  const empfohlen = useEssenceEmpfehlung()
  const abgeschlossen =
    part.state === "output-available" &&
    Boolean((part.output as Abschluss)?.abgeschlossen)
  const client = useQueryClient()

  // The cached essence predates this completion; without a refetch the
  // recommendation would only show after a reload.
  useEffect(() => {
    if (abgeschlossen)
      void client.invalidateQueries({
        queryKey: learningEssenceControllerGetQueryKey(),
      })
  }, [abgeschlossen, client])

  if (!abgeschlossen) return null

  return (
    <div className="flex overflow-hidden rounded-xl border border-[var(--rule)] bg-card shadow-[0_8px_24px_rgba(31,31,31,0.08)]">
      {coach?.portraitUrl ? (
        <img
          src={versionedImageUrl(coach.portraitUrl)}
          alt=""
          loading="lazy"
          className="hidden w-40 shrink-0 object-cover sm:block"
        />
      ) : null}
      <div className="min-w-0 flex-1 p-4">
        <div className="eyebrow">Kennenlernen abgeschlossen</div>
        <div className="mt-1 font-display text-[16px] leading-snug font-medium tracking-tight">
          Deine Lernentwicklung steht
        </div>
        <p className="mt-1 text-[13px] leading-relaxed text-muted-foreground">
          {empfohlen
            ? `Prioritäten, Stärken und Handlungsfelder findest du ab jetzt auf deiner Startseite. Zum Einstieg empfohlen: ${empfohlen.empfehlung.begruendung}`
            : "Prioritäten, Stärken und Handlungsfelder findest du ab jetzt auf deiner Startseite. Wie willst du weitermachen?"}
        </p>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          {empfohlen ? (
            <Button asChild size="sm" className="gap-1.5">
              <Link to={empfohlen.to} search={empfohlen.search}>
                {empfohlen.empfehlung.titel}
                <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            </Button>
          ) : null}
          <Button
            asChild
            size="sm"
            variant={empfohlen ? "outline" : "default"}
            className="gap-1.5"
          >
            <Link to="/durchlauf">
              {GESPRAECHSTRAINING_LABEL} starten
              <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </Button>
          <Button asChild size="sm" variant="outline" className="gap-1.5">
            <Link to="/produkttraining">
              Produkttraining starten
              <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </Button>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            className="gap-1.5 text-muted-foreground"
            onClick={() => void navigate({ to: "/" })}
          >
            <Compass className="h-3.5 w-3.5" />
            Erstmal umsehen
          </Button>
        </div>
      </div>
    </div>
  )
}
